import { useRef, type PointerEvent } from "react";
import { directions, type Movement } from "../movement";
import type { GbaButton, MgbaCore } from "../types";
import MoveControl from "./MoveControl";
import TurboButton from "./TurboButton";

// On-screen controls, shown only for coarse pointers (see .touchpad in CSS).
// `movement` picks the D-pad or the drag stick (see movement.ts). Turbo is
// held; a double tap on it (the button, or the stick's middle) locks it on
// or off instead.
export default function Touchpad({
  core,
  active,
  movement,
  shoulders,
  turboLocked,
  onTurbo,
  onTurboLock,
}: {
  core: MgbaCore | null;
  active: boolean;
  movement: Movement;
  /** Whether L and R are drawn. */
  shoulders: boolean;
  turboLocked: boolean;
  onTurbo: (down: boolean) => void;
  onTurboLock: () => void;
}) {
  const press = (button: GbaButton) => (e: PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!active) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    core?.buttonPress(button);
  };
  const release = (button: GbaButton) => () => core?.buttonUnpress(button);

  // Movement (MoveControl) presses the matching D-pad buttons, diagonals
  // included.
  const held = useRef(new Set<GbaButton>());
  const move = (x: number, y: number) => {
    const d = directions(x, y);
    const next = new Set<GbaButton>();
    if (d.right) next.add("Right");
    if (d.left) next.add("Left");
    if (d.down) next.add("Down");
    if (d.up) next.add("Up");
    for (const button of held.current) if (!next.has(button)) core?.buttonUnpress(button);
    for (const button of next) if (!held.current.has(button)) core?.buttonPress(button);
    held.current = next;
  };

  const pad = (button: GbaButton, label: string, className: string) => (
    <button
      type="button"
      className={`pad ${className}`}
      aria-label={button}
      onPointerDown={press(button)}
      onPointerUp={release(button)}
      onPointerCancel={release(button)}
      onLostPointerCapture={release(button)}
      onContextMenu={(e) => e.preventDefault()}
    >
      {label}
    </button>
  );

  return (
    <div className={`touchpad move-${movement}`}>
      <MoveControl
        mode={movement}
        active={active}
        turboLocked={turboLocked}
        onTurbo={onTurbo}
        onTurboLock={onTurboLock}
        onMove={move}
        label="Direction pad"
      />
      <div className="meta">
        {pad("Select", "select", "small")}
        {pad("Start", "start", "small")}
      </div>
      <div className="ab">
        <span className="b-stack">
          {movement !== "stick-turbo" && (
            <TurboButton active={active} locked={turboLocked} onTurbo={onTurbo} onLock={onTurboLock} />
          )}
          {pad("B", "B", "round")}
        </span>
        {pad("A", "A", "round")}
      </div>
      {shoulders && (
        <div className="shoulders">
          {pad("L", "L", "shoulder")}
          {pad("R", "R", "shoulder")}
        </div>
      )}
    </div>
  );
}
